// CartModal.jsx - Updated with error handling and image upload
import React, { useState } from 'react';
import useDialogFocus from '../utils/useDialogFocus';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Trash2, Plus, Upload, X, Image as ImageIcon } from 'lucide-react';
import './CartModal.css';
import CustomerInfoForm from './CustomerInfoForm';
import ErrorHandler from '../components/ErrorHandler';
import './ValidationStyles.css';
import LazyImage from './LazyImage';
import { uploadCustomizationImage, deleteCustomizationImage, validateImageFile, formatFileSize } from '../utils/imageUploadService';

const CartModal = ({
  isOpen,
  onClose,
  cart,
  groupedCartItems,
  updateQuantity,
  removeFromCart,
  updatePersonalization,
  updateCustomizationImages,
  addToCart,
  catalog,
  totalCost,
  generalNote,
  setGeneralNote,
  handleSubmitOrder,
  orderSubmitting,
  // Customer info props
  showCustomerForm,
  setShowCustomerForm,
  customerInfo,
  setCustomerInfo,
  // Validation props
  formErrors,
  setFormErrors,
  // Error handling props
  orderError,
  dismissOrderError,
  retryOrder,
  // Custom handlers
  handleProceedToCheckout,
  // Product modal handler
  onOpenProductModal
}) => {
  const dialogRef = useDialogFocus(isOpen, onClose);
  // State for managing image uploads
  const [uploadingImages, setUploadingImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  // Handle image file selection
  const handleImageUpload = async (cartItemId, files) => {
    if (!files || files.length === 0) return;

    // Clear any previous errors for this item
    setImageErrors(prev => ({ ...prev, [cartItemId]: '' }));

    // Set uploading state
    setUploadingImages(prev => ({ ...prev, [cartItemId]: true }));

    try {
      const uploadedImages = [];

      // Upload each selected file
      for (const file of Array.from(files)) {
        // Validate the file
        const validation = validateImageFile(file);
        if (!validation.valid) {
          setImageErrors(prev => ({ ...prev, [cartItemId]: validation.error }));
          continue;
        }

        // Upload the file
        const imageData = await uploadCustomizationImage(file, cartItemId);
        uploadedImages.push(imageData);
      }

      // Update the cart item with the new images
      if (uploadedImages.length > 0) {
        updateCustomizationImages(cartItemId, uploadedImages);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setImageErrors(prev => ({
        ...prev,
        [cartItemId]: error.message || 'Failed to upload image'
      }));
    } finally {
      setUploadingImages(prev => ({ ...prev, [cartItemId]: false }));
    }
  };

  // Handle image removal
  const handleRemoveImage = async (cartItemId, imageIndex, imagePath) => {
    try {
      // Delete from storage
      await deleteCustomizationImage(imagePath);

      // Get current item
      const item = cart.find(i => i.cartItemId === cartItemId);
      if (!item || !item.customizationImages) return;

      // Remove from array
      const updatedImages = item.customizationImages.filter((_, idx) => idx !== imageIndex);
      updateCustomizationImages(cartItemId, updatedImages, true);
    } catch (error) {
      console.error('Error removing image:', error);
      setImageErrors(prev => ({
        ...prev,
        [cartItemId]: 'Failed to remove image'
      }));
    }
  };

  // If the modal is not open, don't render anything
  if (!isOpen) return null;

  // Handle back to cart
  const handleBackToCart = () => {
    setShowCustomerForm(false);
    
    // Also clear any order error when going back to cart
    if (orderError) {
      dismissOrderError();
    }
  };
  
  // Count total items in cart
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Network status check
  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

  // Handle click on cart item to open product modal
  const handleCartItemClick = (productId) => {
    if (onOpenProductModal) {
      const product = catalog.find(p => p.id.toString() === productId.toString());
      if (product) {
        onOpenProductModal(product);
      }
    }
  };

  return (
    <div className="cart-modal-overlay">
      <div className="cart-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="cart-heading">
        <div className="cart-modal-header">
          <h3 id="cart-heading">{showCustomerForm ? "Checkout" : "Your Cart"}</h3>
          <button className="close-modal-btn" aria-label="Close cart" onClick={onClose}>✕</button>
        </div>

        <div className="cart-modal-content">
          {/* Display network error if offline */}
          {isOffline && (
            <div className="network-error-banner">
              <span className="network-error-icon">⚠️</span>
              <span>You appear to be offline. Please check your internet connection.</span>
            </div>
          )}
          
          {/* Display order error if present */}
          {orderError && (
            <ErrorHandler
              error={orderError}
              resetError={dismissOrderError}
              retryAction={retryOrder}
            />
          )}
          
          {showCustomerForm ? (
            <CustomerInfoForm 
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              onSubmit={handleSubmitOrder}
              onCancel={handleBackToCart}
              isSubmitting={orderSubmitting}
              // Pass validation errors
              errors={{
                name: formErrors.customerName,
                phone: formErrors.customerPhone,
                email: formErrors.customerEmail
              }}
              setErrors={(errors) => {
                // Map CustomerInfoForm errors back to the parent component's format
                setFormErrors({
                  ...formErrors,
                  customerName: errors.name,
                  customerPhone: errors.phone,
                  customerEmail: errors.email
                });
              }}
              // Disable submission if offline
              isOffline={isOffline}
            />
          ) : (
            <>
              {cart.length === 0 ? (
                <div className="empty-cart">
                  <p>Nothing personal in here yet.</p>
                  {formErrors.cartItems && (
                    <div className="error-message cart-error">{formErrors.cartItems}</div>
                  )}
                  <Button onClick={onClose}>Continue Shopping</Button>
                </div>
              ) : (
                <>
                  {/* Display the cart items grouped by product */}
                  {Object.entries(groupedCartItems).map(([productId, product]) => (
                    <div key={productId} className="product-group">
                      <div className="product-group-header">
                        <div
                          className="product-group-image-wrapper"
                          onClick={() => handleCartItemClick(productId)}
                          title="Click to view product details"
                        >
                          <LazyImage
                            src={
                              product.media && product.media.length > 0
                                ? product.media[0].url
                                : product.image || '/images/placeholder.jpg'
                            }
                            alt={product.name}
                            className="product-group-image"
                            fallbackSrc="/images/placeholder.jpg"
                          />
                        </div>
                        <h4
                          className="product-group-title"
                          data-price={product.pricePending ? 'Price to be confirmed' : `R${product.price} each`}
                          onClick={() => handleCartItemClick(productId)}
                          title="Click to view product details"
                        >
                          {product.name}
                        </h4>
                      </div>

                      {product.items.map((item, index) => (
                        <div key={item.cartItemId} className="cart-item-card">
                          <div className="cart-item-header">
                            <span className="item-instance-label">Item {index + 1}</span>
                            <span className="item-quantity-label">Qty: {item.quantity}</span>
                            <button
                              className="remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromCart(item.cartItemId);
                              }}
                              aria-label="Remove item"
                              title="Remove from cart"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>

                          <div className="personalization-field">
                            <label>Personalization:</label>
                            {item.personalisationDetails && (
                              <div className="structured-personalisation-summary">
                                <span><strong>Name:</strong> {item.personalisationDetails.name}</span>
                                <span><strong>Message:</strong> {item.personalisationDetails.message}</span>
                              </div>
                            )}
                            <textarea
                              value={item.personalization}
                              onChange={(e) => updatePersonalization(item.cartItemId, e.target.value)}
                              placeholder={`Enter personalization details for this item...`}
                              aria-label="Personalization details"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Image Upload Section */}
                          <div className="customization-images-section">
                            <label className="images-label">
                              <ImageIcon size={16} />
                              Customization Images (Optional):
                            </label>

                            {/* Display uploaded images */}
                            {item.customizationImages && item.customizationImages.length > 0 && (
                              <div className="uploaded-images-grid">
                                {item.customizationImages.map((image, idx) => (
                                  <div key={idx} className="uploaded-image-item">
                                    <img
                                      src={image.url}
                                      alt={`Customization ${idx + 1}`}
                                      className="uploaded-image-thumb"
                                    />
                                    <div className="uploaded-image-info">
                                      <span className="uploaded-image-name">{image.name}</span>
                                      <span className="uploaded-image-size">{formatFileSize(image.size)}</span>
                                    </div>
                                    <button
                                      className="remove-image-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveImage(item.cartItemId, idx, image.path);
                                      }}
                                      aria-label="Remove image"
                                      title="Remove image"
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Upload button */}
                            <div className="image-upload-controls">
                              <label className="image-upload-btn">
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handleImageUpload(item.cartItemId, e.target.files)}
                                  style={{ display: 'none' }}
                                  disabled={uploadingImages[item.cartItemId]}
                                />
                                <Upload size={16} />
                                {uploadingImages[item.cartItemId] ? 'Uploading...' : 'Upload Images'}
                              </label>
                              <span className="image-upload-hint">
                                Max 3MB per image. JPG, PNG, GIF, WebP
                              </span>
                            </div>

                            {/* Display upload errors */}
                            {imageErrors[item.cartItemId] && (
                              <div className="image-error-message">
                                {imageErrors[item.cartItemId]}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {product.collectionSlug ? (
                        <Link className="add-another-btn" to={`/collections/${product.collectionSlug}/product/${productId}`} onClick={onClose}>
                          <Plus size={16} /> Configure another
                        </Link>
                      ) : (
                        <button
                          className="add-another-btn"
                          onClick={() => addToCart(catalog.find(c => c.id.toString() === productId.toString()))}
                          disabled={isOffline}
                        >
                          <Plus size={16} /> Add another
                        </button>
                      )}
                    </div>
                  ))}

                  {/* General Order Notes */}
                  <div className="order-notes-section">
                    <label htmlFor="order-notes">Order Notes (Optional):</label>
                    <textarea
                      id="order-notes"
                      value={generalNote}
                      onChange={(e) => setGeneralNote(e.target.value)}
                      className="cart-textarea"
                      placeholder="Add any special instructions or notes about your order..."
                      aria-label="Order notes"
                    />
                  </div>

                  {/* Cart Summary Footer */}
                  <div className="cart-summary-footer">
                    <div className="summary-row">
                      <span className="summary-label">Total Items:</span>
                      <span className="summary-value">{totalItems}</span>
                    </div>
                    <div className="summary-row total-row">
                      <span className="summary-label">Total:</span>
                      <span className="summary-value">{cart.some(item => item.pricePending) ? 'Price to be confirmed' : `R${totalCost.toFixed(2)}`}</span>
                    </div>

                    {formErrors.cartItems && (
                      <div className="error-message cart-error">{formErrors.cartItems}</div>
                    )}

                    <Button
                      className="cart-send-button"
                      onClick={handleProceedToCheckout}
                      disabled={cart.length === 0 || isOffline || cart.some(item => item.pricePending)}
                    >
                      {isOffline ? 'Cannot checkout while offline' : cart.some(item => item.pricePending) ? 'Checkout available once pricing is confirmed' : 'Proceed to Checkout'}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartModal;
